import React from 'react';
import { Trash2, Copy } from 'lucide-react';
import { FIELD_TYPE_LIST } from './utils/fieldTypes';

const FieldPropertiesPanel = ({
  field,
  roles,
  onUpdateField,
  onDeleteField,
  onDuplicateField,
}) => {
  if (!field) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Field Properties</h3>
        <p className="text-sm text-gray-500">
          Select a field to edit its properties
        </p>
      </div>
    );
  }

  const handleNameChange = (e) => {
    onUpdateField(field.uuid, { name: e.target.value });
  };

  const handleTypeChange = (e) => {
    onUpdateField(field.uuid, { type: e.target.value });
  };

  const handleRequiredChange = (e) => {
    onUpdateField(field.uuid, { required: e.target.checked });
  };

  const handleRoleChange = (e) => {
    onUpdateField(field.uuid, { roleUuid: e.target.value });
  };

  const role = roles.find(r => r.uuid === field.roleUuid);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Field Properties</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onDuplicateField(field.uuid)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title="Duplicate field"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDeleteField(field.uuid)}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete field"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Field Name */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Name
          </label>
          <input
            type="text"
            value={field.name}
            onChange={handleNameChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
            placeholder="Field name"
          />
        </div>

        {/* Field Type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Type
          </label>
          <select
            value={field.type}
            onChange={handleTypeChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
          >
            {FIELD_TYPE_LIST.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* Assigned Role */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Assigned to
          </label>
          <select
            value={field.roleUuid || ''}
            onChange={handleRoleChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
          >
            {roles.map((r) => (
              <option key={r.uuid} value={r.uuid}>
                {r.name}
              </option>
            ))}
          </select>
          {role && (
            <div className="flex items-center gap-2 mt-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: role.color }}
              />
              <span className="text-xs text-gray-500">
                {role.name} will fill this field
              </span>
            </div>
          )}
        </div>

        {/* Required Toggle */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={field.required}
              onChange={handleRequiredChange}
              className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
            />
            <span className="text-sm text-gray-700">Required field</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Signers must fill this field to complete signing
          </p>
        </div>

        {/* Position Info (read-only) */}
        {field.areas[0] && (
          <div className="pt-3 border-t border-gray-200">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Position
            </label>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
              <div>X: {(field.areas[0].x * 100).toFixed(1)}%</div>
              <div>Y: {(field.areas[0].y * 100).toFixed(1)}%</div>
              <div>W: {(field.areas[0].w * 100).toFixed(1)}%</div>
              <div>H: {(field.areas[0].h * 100).toFixed(1)}%</div>
              <div className="col-span-2">Page: {field.areas[0].page}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldPropertiesPanel;
